import { useCallback, useMemo, useState } from 'react';

import { getStorageItem, setStorageItem } from '@/utils/storage/local-storage';
import Checkbox from '@ui/checkbox/checkbox';
import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import GenericModalFooter from '@ui/modal/generic-modal-footer';
import Modal from '@ui/modal/modal';
import Text from '@ui/text/text/text';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getActiveChannel } from '@/store/slices/user/selectors';
import { saveWorkingProjectByFields, setShowUpdateProjectTypeModal } from '@/store/slices/project';
import useOnFinishSave from '@/utils/use-on-finish-save';
import {
  getWorkingProjectUuid,
  selectShowUpdateProjectTypeModal,
  selectWorkingProjectType,
} from '@/store/slices/project/selectors';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';

const SAVE_ID = 'convert-project-type';

const UpdateProjectTypeModal = () => {
  const newType = useAppSelector(selectShowUpdateProjectTypeModal);
  const channel = useAppSelector(getActiveChannel);
  const projectUuid = useAppSelector(getWorkingProjectUuid);
  const currentProjectType = useAppSelector(selectWorkingProjectType);

  const dispatch = useAppDispatch();

  const handleClose = useCallback(() => {
    dispatch(setShowUpdateProjectTypeModal(null));
  }, [dispatch]);

  const { saving } = useOnFinishSave({ saveId: SAVE_ID, onComplete: handleClose });

  const initShouldShowModal = useMemo(() => {
    const storedValue = getStorageItem(`shouldShowUpdateProjectTypeModal-${channel}`);

    return storedValue === 'true';
  }, [channel]);

  const [shouldShowUpdateProjectTypeModal, setShouldShowUpdateProjectTypeModal] = useState(initShouldShowModal);

  const handleShouldShowMessageToggle = (_: unknown, isOn: boolean) => {
    setShouldShowUpdateProjectTypeModal(isOn);
  };

  const handleSave = useCallback(() => {
    if (!newType || !projectUuid) return;

    if (shouldShowUpdateProjectTypeModal) {
      setStorageItem(`shouldShowUpdateProjectTypeModal-${channel}`, 'true');
    }

    dispatch(saveWorkingProjectByFields({ event_type: newType, saveId: SAVE_ID }));
  }, [newType, projectUuid, shouldShowUpdateProjectTypeModal, dispatch, channel]);

  const handleTransitionClosed = useCallback(() => {
    setShouldShowUpdateProjectTypeModal(initShouldShowModal);
  }, [initShouldShowModal]);

  return (
    <Modal
      open={!!newType}
      onRequestClose={saving ? undefined : handleClose}
      onTransitionClosed={handleTransitionClosed}
      customSize={{
        minHeight: '100px',
        height: '336px',
        maxHeight: 'calc(100vh - 16px)',
        width: '640px',
        maxWidth: 'calc(100vw - 16px)',
      }}
      RenderFooterContent={() => {
        return (
          <Container
            borderRadius="0 0 12px 12px"
            background="var(--gray-900)"
            padding="0 40px 24px 40px"
            className="flex ai-center jc-space-between overflow-hidden"
          >
            <Container background="var(--gray-900)" width="100%" className="flex-1">
              <Container background="var(--gray-900)" width="fit-content">
                <Checkbox
                  disabled={saving}
                  value={'shouldShowUpdateProjectTypeModal'}
                  checked={shouldShowUpdateProjectTypeModal}
                  onChange={handleShouldShowMessageToggle}
                  size={12}
                  label={
                    <Text whiteSpace="normal" maxWidth="210px" clamp={2} className="L1 margin-l-4 no-select">
                      Don't show this message again
                    </Text>
                  }
                />
              </Container>
            </Container>
            <div>
              <GenericModalFooter
                handleClose={saving ? () => null : handleClose}
                onSave={handleSave}
                disableCancel={saving}
                backgroundColor="var(--gray-900)"
                saveText="Yes, convert"
                saving={saving}
              />
            </div>
          </Container>
        );
      }}
    >
      <Container background="var(--gray-900)" padding="24px 40px" height="100%" borderRadius="12px">
        <Text tag="h4" className="H4 margin-b-4">
          Convert to event
        </Text>
        <Text tag="p" className="P1 margin-b-16" color="var(--gray-300)">
          Transform your {currentProjectType} into a multi-session event
        </Text>

        <Container margin="24px 0 16px 0" className="flex ai-center" gap="37px">
          <Container
            padding="12px"
            borderRadius="12px"
            background="var(--gray-800)"
            className="flex-1 flex ai-center jc-center"
            height="64px"
          >
            <Text color={getThemeColorCSSVariable()} className="S1">
              {currentProjectType}
            </Text>
          </Container>
          <div>
            <Icon name={ICONS.ARROW_RIGHT} size={16} />
          </div>
          <Container
            padding="12px"
            borderRadius="12px"
            background="var(--gray-800)"
            className="flex-1 flex ai-center jc-center"
            height="64px"
          >
            <Text color={getThemeColorCSSVariable()} className="S1">
              {newType}
            </Text>
          </Container>
        </Container>

        <Text tag="p" className="S1">
          Explanation how to revert the changes
        </Text>
      </Container>
    </Modal>
  );
};

export default UpdateProjectTypeModal;
